import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useImpersonationStore } from '@/stores/impersonation'
import { useWorkspaceStore } from '@/stores/data'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, _from, savedPosition) {
    if (savedPosition) return savedPosition
    if (to.hash) {
      if (typeof document !== 'undefined' && document.querySelector(to.hash)) {
        return { el: to.hash, behavior: 'smooth' }
      }
      return { top: 0 }
    }
    return { top: 0 }
  },
  routes: [
    {
      path: '/',
      name: 'landing',
      component: () => import('@/views/LandingView.vue'),
      meta: { public: true }
      // No title - keeps the HTML default "Sevfy | Simple DRM"
    },
    {
      path: '/impersonate',
      name: 'impersonate',
      component: () => import('@/views/misc/ImpersonateView.vue'),
      meta: { public: true }
    },
    {
      path: '/dashboard',
      name: 'dashboard',
      component: () => import('@/views/misc/DashboardView.vue'),
      meta: { requiresAuth: true, title: 'Dashboard' }
    },
    {
      path: '/d/:slug',
      name: 'dashboard-page',
      component: () => import('@/views/misc/DashboardView.vue'),
      meta: { requiresAuth: true }
      // Title set dynamically by DashboardView based on the loaded page name
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/auth/LoginView.vue'),
      meta: { requiresGuest: true, title: 'Sign In' }
    },
    {
      path: '/register',
      name: 'register',
      component: () => import('@/views/auth/RegisterView.vue'),
      meta: { requiresGuest: true, title: 'Sign Up' }
    },
    {
      path: '/verify-email',
      name: 'verify-email',
      component: () => import('@/views/auth/VerifyEmailView.vue'),
      meta: { requiresAuth: true, skipVerification: true, title: 'Verify Email' }
    },
    {
      path: '/auth/action',
      name: 'auth-action',
      component: () => import('@/views/auth/AuthActionView.vue'),
      meta: { public: true, title: 'Account' }
    },
    {
      path: '/lists',
      name: 'lists',
      component: () => import('@/views/lists/ListsView.vue'),
      meta: { requiresAuth: true, title: 'Lists' }
    },
    {
      path: '/entries',
      name: 'all-entries',
      component: () => import('@/views/lists/AllEntriesView.vue'),
      meta: { requiresAuth: true, title: 'Entries' }
    },
    {
      path: '/lists/:listId',
      name: 'list-detail',
      component: () => import('@/views/lists/ListDetailView.vue'),
      meta: { requiresAuth: true }
      // Dynamic: "List - {name}" - set by ListDetailView
    },
    {
      path: '/lists/:listId/entries',
      name: 'list-entries',
      component: () => import('@/views/lists/EntriesView.vue'),
      meta: { requiresAuth: true }
      // Dynamic: "Entries - {list name}" - set by EntriesView
    },
    {
      path: '/lists/:listId/entries/:entryId',
      name: 'entry-detail',
      component: () => import('@/views/lists/EntryDetailView.vue'),
      meta: { requiresAuth: true }
      // Dynamic: "Entry - {shortId}" - set by EntryDetailView
    },
    {
      path: '/lists/:listId/batches',
      name: 'list-batches',
      component: () => import('@/views/lists/BatchesView.vue'),
      meta: { requiresAuth: true }
      // Dynamic: "Batches - {list name}" - set by BatchesView
    },
    {
      path: '/profile',
      name: 'profile',
      component: () => import('@/views/workspace/ProfileView.vue'),
      meta: { requiresAuth: true, title: 'Profile' }
    },
    {
      path: '/workspaces',
      name: 'workspaces',
      component: () => import('@/views/workspace/WorkspacesView.vue'),
      meta: { requiresAuth: true, title: 'Workspaces' }
    },
    {
      path: '/billing',
      name: 'billing',
      component: () => import('@/views/workspace/BillingView.vue'),
      meta: { requiresAuth: true, title: 'Billing' }
    },
    {
      path: '/settings/email',
      name: 'email-settings',
      component: () => import('@/views/workspace/EmailSettingsView.vue'),
      meta: { requiresAuth: true, title: 'Email Settings' }
    },
    {
      path: '/forms',
      name: 'forms',
      component: () => import('@/views/gather-share/FormsView.vue'),
      meta: { requiresAuth: true, title: 'Forms' }
    },
    {
      path: '/flows',
      name: 'flows',
      component: () => import('@/views/flows/GlobalFlowsView.vue'),
      meta: { requiresAuth: true, title: 'Flows' }
    },
    {
      path: '/forms/new',
      name: 'form-new',
      component: () => import('@/views/gather-share/FormBuilderView.vue'),
      meta: { requiresAuth: true, title: 'New Form' }
    },
    {
      path: '/forms/:formId',
      name: 'form-edit',
      component: () => import('@/views/gather-share/FormBuilderView.vue'),
      meta: { requiresAuth: true }
      // Dynamic: "Form - {name}" - set by FormBuilderView
    },
    {
      path: '/sheets',
      name: 'sheets',
      component: () => import('@/views/gather-share/SheetsView.vue'),
      meta: { requiresAuth: true, title: 'Sheets' }
    },
    {
      path: '/sheets/new',
      name: 'sheet-new',
      component: () => import('@/views/gather-share/SheetBuilderView.vue'),
      meta: { requiresAuth: true, title: 'New Sheet' }
    },
    {
      path: '/sheets/:sheetId',
      name: 'sheet-edit',
      component: () => import('@/views/gather-share/SheetBuilderView.vue'),
      meta: { requiresAuth: true }
      // Dynamic: "Sheet - {name}" - set by SheetBuilderView
    },
    {
      path: '/s/:token/sheets',
      name: 'public-sheet-batches',
      component: () => import('@/views/public/PublicSheetBatchPickerView.vue'),
      meta: { public: true }
    },
    {
      path: '/s/:token',
      name: 'sheet-page',
      component: () => import('@/views/gather-share/SheetPage.vue'),
      meta: { public: true }
      // Dynamic: "{title or name or token}" - set by SheetPage
    },
    {
      path: '/charts',
      name: 'charts',
      component: () => import('@/views/gather-share/ChartsView.vue'),
      meta: { requiresAuth: true, title: 'Charts' }
    },
    {
      path: '/charts/new',
      name: 'chart-new',
      component: () => import('@/views/gather-share/ChartBuilderView.vue'),
      meta: { requiresAuth: true, title: 'New Chart' }
    },
    {
      path: '/charts/:chartId',
      name: 'chart-edit',
      component: () => import('@/views/gather-share/ChartBuilderView.vue'),
      meta: { requiresAuth: true }
      // Dynamic: "Chart - {name}" - set by ChartBuilderView
    },
    {
      path: '/c/:token/charts',
      name: 'public-chart-batches',
      component: () => import('@/views/public/PublicChartBatchPickerView.vue'),
      meta: { public: true }
    },
    {
      path: '/c/:token',
      name: 'chart-page',
      component: () => import('@/views/gather-share/ChartPage.vue'),
      meta: { public: true }
      // Dynamic: "{title or name or token}" - set by ChartPage
    },
    {
      path: '/f/:token/forms',
      name: 'public-form-batches',
      component: () => import('@/views/public/PublicBatchPickerView.vue'),
      meta: { public: true }
    },
    {
      path: '/f/:token',
      name: 'public-form',
      component: () => import('@/views/public/PublicFormView.vue'),
      meta: { public: true }
      // Dynamic: "{title or name or token}" - set by PublicFormView
    },
    // ── Internal (authenticated) G&S views - usable as panels/widgets ────────
    {
      path: '/internal/f/:token',
      name: 'internal-form',
      component: () => import('@/views/gather-share/InternalFormView.vue'),
      meta: { requiresAuth: true, title: 'Form' }
    },
    {
      path: '/internal/s/:token',
      name: 'internal-sheet',
      component: () => import('@/views/gather-share/InternalSheetView.vue'),
      meta: { requiresAuth: true, title: 'Sheet' }
    },
    {
      path: '/internal/c/:token',
      name: 'internal-chart',
      component: () => import('@/views/gather-share/InternalChartView.vue'),
      meta: { requiresAuth: true, title: 'Chart' }
    },
    {
      path: '/accept-invite',
      name: 'accept-invite',
      component: () => import('@/views/auth/AcceptInviteView.vue'),
      meta: { public: true, title: 'Accept Invite' }
    },
    {
      path: '/templates',
      name: 'templates',
      component: () => import('@/views/public/PublicTemplatesCatalogView.vue'),
      meta: { public: true, title: 'Workspace Templates' }
    },
    {
      path: '/templates/manage',
      name: 'templates-manage',
      component: () => import('@/views/templates/TemplatesView.vue'),
      meta: { requiresAuth: true, title: 'My Templates' }
    },
    {
      path: '/templates/edit/:id',
      name: 'template-editor',
      component: () => import('@/views/templates/TemplateEditorView.vue'),
      meta: { requiresAuth: true, title: 'Edit Template' }
    },
    {
      path: '/templates/:slug',
      name: 'template-public',
      component: () => import('@/views/public/PublicTemplateView.vue'),
      meta: { public: true }
      // Title set dynamically from template name
    },
    {
      path: '/notifications',
      name: 'notifications',
      component: () => import('@/views/misc/NotificationsView.vue'),
      meta: { requiresAuth: true, title: 'Notifications' }
    },
    {
      path: '/flags',
      name: 'flags',
      component: () => import('@/views/lists/FlagsView.vue'),
      meta: { requiresAuth: true, title: 'Review Queue' }
    },
    {
      path: '/lists/:listId/flows',
      name: 'list-flows',
      component: () => import('@/views/flows/FlowsView.vue'),
      meta: { requiresAuth: true }
      // Dynamic: "Flows - {list name}" - set by FlowsView
    },
    {
      path: '/lists/:listId/flows/:flowId',
      name: 'flow-detail',
      component: () => import('@/views/flows/FlowDetailView.vue'),
      meta: { requiresAuth: true }
      // Dynamic: "Flow - {name}" or "New Flow" - set by FlowDetailView
    },
    {
      path: '/help',
      name: 'help',
      component: () => import('@/views/misc/HelpCenterView.vue'),
      meta: { public: true, title: 'Help' }
    },
    {
      path: '/privacy',
      name: 'privacy',
      component: () => import('@/views/misc/PrivacyView.vue'),
      meta: { public: true, title: 'Privacy' }
    },
    {
      path: '/terms',
      name: 'terms',
      component: () => import('@/views/misc/TermsView.vue'),
      meta: { public: true, title: 'Terms of Service' }
    },
    // Use-case landing pages - SEO-targeted, data-driven, one component per slug
    {
      path: '/use-cases',
      name: 'use-cases',
      component: () => import('@/views/landing-pages/UseCasesIndexView.vue'),
      meta: { public: true, title: 'Use Cases' }
    },
    {
      path: '/use-cases/:slug',
      name: 'use-case',
      component: () => import('@/views/landing-pages/UseCaseLandingView.vue'),
      meta: { public: true }
      // Title set dynamically from use-cases.json metaTitle
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'error',
      component: () => import('@/views/misc/ErrorView.vue'),
      meta: { public: true, title: 'Not Found' }
    }
  ]
})

// Apply static page titles from route meta.
// Dynamic views override this with usePageTitle().setTitle() after data loads.
router.afterEach((to) => {
  const title = (to.meta as Record<string, unknown>).title as string | undefined
  if (title) {
    document.title = `Sevfy | ${title}`
  }
})

// Navigation guards
router.beforeEach(async (to, _from) => {
  const authStore = useAuthStore()
  const impersonationStore = useImpersonationStore()
  const workspaceStore = useWorkspaceStore()

  // Wait for auth to initialize
  if (!authStore.isInitialized) {
    await authStore.initAuth()
  }

  const isAuthenticated = authStore.isAuthenticated
  const isImpersonating = impersonationStore.isImpersonating

  // Ensure workspace data is loaded for any authenticated session.
  // This handles deep-link logins where App.vue onMounted ran before the
  // user was authenticated and therefore skipped loadWorkspaces().
  // Catch errors so a transient API failure (e.g. new Google user whose
  // /auth/sync hasn't completed yet) doesn't abort the navigation and
  // trigger the global 401 handler, which would log the user out.
  if (isAuthenticated && !isImpersonating) {
    try {
      await workspaceStore.loadWorkspaces()
    } catch {
      // Non-fatal - workspace data will be retried on the next navigation
    }
  }

  // If visiting landing page while authenticated (or impersonating), redirect to dashboard
  if (to.name === 'landing' && (isAuthenticated || isImpersonating)) {
    return { name: 'dashboard' }
  }

  // Impersonation sessions can access any requiresAuth route
  if (to.meta.requiresAuth && isImpersonating) {
    return // allow through
  }

  if (to.meta.requiresAuth && !isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } }
  }

  // Redirect unverified users to the verification page
  if (to.meta.requiresAuth && isAuthenticated && !authStore.isEmailVerified && !to.meta.skipVerification) {
    return { name: 'verify-email' }
  }

  // Don't let verified users visit the verify-email page
  if (to.name === 'verify-email' && isAuthenticated && authStore.isEmailVerified) {
    return { name: 'dashboard' }
  }

  if (to.meta.requiresGuest && isAuthenticated) {
    return { name: 'dashboard' }
  }
})

export default router
